import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff,
  Key,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  User,
  Activity
} from 'lucide-react';

const APIKeysManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSecrets, setShowSecrets] = useState({});

  // Mock API key data
  const mockApiKeys = [
    {
      id: 1,
      name: 'Mobile App API Key',
      keyId: 'ak_1234567890abcdef',
      secret: 'sk_abcdef1234567890abcdef1234567890',
      permissions: ['tickets:read', 'tickets:write', 'users:read'],
      status: 'active',
      createdBy: 'John Doe',
      createdAt: '2024-01-10T09:00:00Z',
      lastUsed: '2024-01-15T14:30:00Z',
      usageCount: 1250,
      rateLimit: 1000,
      expiresAt: '2024-12-31T23:59:59Z',
      description: 'API key for mobile application integration',
      ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8'],
      userAgent: 'MobileApp/1.0.0'
    },
    {
      id: 2,
      name: 'Webhook Integration',
      keyId: 'ak_webhook123456789',
      secret: 'sk_webhook1234567890abcdef1234567890',
      permissions: ['webhooks:read', 'webhooks:write'],
      status: 'active',
      createdBy: 'Jane Smith',
      createdAt: '2024-01-12T11:15:00Z',
      lastUsed: '2024-01-15T16:45:00Z',
      usageCount: 890,
      rateLimit: 500,
      expiresAt: null,
      description: 'API key for webhook management system',
      ipWhitelist: ['203.0.113.0/24'],
      userAgent: 'WebhookBot/2.1.0'
    },
    {
      id: 3,
      name: 'Reporting Dashboard',
      keyId: 'ak_reporting123456',
      secret: 'sk_reporting1234567890abcdef1234567890',
      permissions: ['reports:read', 'analytics:read'],
      status: 'expired',
      createdBy: 'Mike Johnson',
      createdAt: '2023-12-01T08:30:00Z',
      lastUsed: '2024-01-05T10:20:00Z',
      usageCount: 2100,
      rateLimit: 2000,
      expiresAt: '2024-01-01T00:00:00Z',
      description: 'API key for reporting dashboard access',
      ipWhitelist: [],
      userAgent: 'ReportDashboard/1.5.0'
    },
    {
      id: 4,
      name: 'Third-party Integration',
      keyId: 'ak_thirdparty12345',
      secret: 'sk_thirdparty1234567890abcdef1234567890',
      permissions: ['tickets:read', 'users:read', 'departments:read'],
      status: 'revoked',
      createdBy: 'Sarah Wilson',
      createdAt: '2024-01-08T14:20:00Z',
      lastUsed: '2024-01-14T09:15:00Z',
      usageCount: 450,
      rateLimit: 100,
      expiresAt: '2024-06-30T23:59:59Z',
      description: 'API key for third-party service integration',
      ipWhitelist: ['198.51.100.0/24'],
      userAgent: 'ThirdPartyApp/3.2.1'
    }
  ];

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = () => {
    setLoading(true);
    try {
      setApiKeys(mockApiKeys);
      setLoading(false);
    } catch (err) {
      setError('Failed to load API keys');
      setLoading(false);
    }
  };

  const handleCreateApiKey = (keyData) => {
    const newKey = {
      id: Date.now(),
      ...keyData,
      keyId: `ak_${Math.random().toString(36).substr(2, 16)}`,
      secret: `sk_${Math.random().toString(36).substr(2, 32)}`,
      status: 'active',
      createdAt: new Date().toISOString(),
      lastUsed: null,
      usageCount: 0
    };
    setApiKeys([...apiKeys, newKey]);
    setSuccess('API key created successfully');
    setShowForm(false);
  };

  const handleUpdateApiKey = (keyData) => {
    setApiKeys(apiKeys.map(k => 
      k.id === editingKey.id ? { ...k, ...keyData } : k
    ));
    setSuccess('API key updated successfully');
    setEditingKey(null);
    setShowForm(false);
  };

  const handleDeleteApiKey = (id) => {
    if (window.confirm('Are you sure you want to delete this API key?')) {
      setApiKeys(apiKeys.filter(k => k.id !== id));
      setSuccess('API key deleted successfully');
    }
  };

  const handleRevokeApiKey = (id) => {
    setApiKeys(apiKeys.map(k => 
      k.id === id ? { ...k, status: 'revoked' } : k
    ));
    setSuccess('API key revoked successfully');
  };

  const handleRegenerateSecret = (id) => {
    setApiKeys(apiKeys.map(k => 
      k.id === id ? { 
        ...k, 
        secret: `sk_${Math.random().toString(36).substr(2, 32)}`,
        lastUsed: null,
        usageCount: 0
      } : k
    ));
    setSuccess('API key secret regenerated successfully');
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };

  const toggleSecretVisibility = (id) => {
    setShowSecrets(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredApiKeys = apiKeys.filter(apiKey => {
    const matchesSearch = apiKey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         apiKey.keyId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         apiKey.createdBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || apiKey.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      expired: 'warning',
      revoked: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">API Keys Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage API keys for system integrations and third-party applications
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create API Key
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="default" className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Keys</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{apiKeys.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.filter(k => k.status === 'active').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Expired</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.filter(k => k.status === 'expired').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Revoked</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {apiKeys.filter(k => k.status === 'revoked').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search API keys..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <div className="space-y-4">
        {filteredApiKeys.map((apiKey) => (
          <Card key={apiKey.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{apiKey.name}</CardTitle>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {apiKey.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(apiKey.status)}
                  {getStatusBadge(apiKey.status)}
                  {isExpired(apiKey.expiresAt) && (
                    <Badge variant="destructive">Expired</Badge>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Key Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Key ID</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono text-gray-800 dark:text-gray-200">
                      {apiKey.keyId}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => handleCopyToClipboard(apiKey.keyId)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Secret</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono text-gray-800 dark:text-gray-200">
                      {showSecrets[apiKey.id] 
                        ? apiKey.secret 
                        : `${apiKey.secret.substring(0, 8)}...${apiKey.secret.substring(apiKey.secret.length - 8)}`
                      }
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => toggleSecretVisibility(apiKey.id)}
                    >
                      {showSecrets[apiKey.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleCopyToClipboard(apiKey.secret)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissions</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {apiKey.permissions.map((permission, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {permission}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Usage Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Usage Count:</span>
                  <span className="ml-2 font-medium">{apiKey.usageCount.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Rate Limit:</span>
                  <span className="ml-2 font-medium">{apiKey.rateLimit}/hour</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Created:</span>
                  <span className="ml-2 font-medium">{formatDate(apiKey.createdAt)}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Last Used:</span>
                  <span className="ml-2 font-medium">
                    {apiKey.lastUsed ? formatDate(apiKey.lastUsed) : 'Never'}
                  </span>
                </div>
              </div>

              {/* Additional Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Created By:</span>
                  <span className="ml-2 font-medium">{apiKey.createdBy}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Expires:</span>
                  <span className="ml-2 font-medium">
                    {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Never'}
                  </span>
                </div>
              </div>

              {/* IP Whitelist */}
              {apiKey.ipWhitelist.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">IP Whitelist</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {apiKey.ipWhitelist.map((ip, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {ip}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingKey(apiKey);
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRegenerateSecret(apiKey.id)}
                  className="flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Regenerate
                </Button>
                {apiKey.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRevokeApiKey(apiKey.id)}
                    className="flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Revoke
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteApiKey(apiKey.id)}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredApiKeys.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No API keys found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search criteria'
                : 'Create your first API key to get started'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* API Key Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>
                {editingKey ? 'Edit API Key' : 'Create New API Key'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <APIKeyForm
                apiKey={editingKey}
                onSubmit={editingKey ? handleUpdateApiKey : handleCreateApiKey}
                onCancel={() => {
                  setShowForm(false);
                  setEditingKey(null);
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// API Key Form Component
const APIKeyForm = ({ apiKey, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: apiKey?.name || '',
    description: apiKey?.description || '',
    permissions: apiKey?.permissions || [],
    rateLimit: apiKey?.rateLimit || 1000,
    expiresAt: apiKey?.expiresAt ? apiKey.expiresAt.split('T')[0] : '',
    ipWhitelist: apiKey?.ipWhitelist || [],
    userAgent: apiKey?.userAgent || ''
  });

  const availablePermissions = [
    'tickets:read',
    'tickets:write',
    'tickets:delete',
    'users:read',
    'users:write',
    'departments:read',
    'departments:write',
    'reports:read',
    'analytics:read',
    'webhooks:read',
    'webhooks:write',
    'settings:read',
    'settings:write'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      expiresAt: formData.expiresAt ? `${formData.expiresAt}T23:59:59Z` : null
    };
    onSubmit(submitData);
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const handleIpWhitelistChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      ipWhitelist: prev.ipWhitelist.map((ip, i) => i === index ? value : ip)
    }));
  };

  const addIpWhitelist = () => {
    setFormData(prev => ({
      ...prev,
      ipWhitelist: [...prev.ipWhitelist, '']
    }));
  };

  const removeIpWhitelist = (index) => {
    setFormData(prev => ({
      ...prev,
      ipWhitelist: prev.ipWhitelist.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Key Name
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter API key name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe what this API key is used for"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            rows={3}
          />
        </div>
      </div>

      {/* Permissions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Permissions
        </label>
        <div className="grid grid-cols-2 gap-2">
          {availablePermissions.map((permission) => (
            <label key={permission} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.permissions.includes(permission)}
                onChange={() => handlePermissionToggle(permission)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{permission}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rate Limit (requests/hour)
          </label>
          <Input
            type="number"
            value={formData.rateLimit}
            onChange={(e) => setFormData(prev => ({ ...prev, rateLimit: parseInt(e.target.value) }))}
            min="1"
            max="10000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Expiration Date
          </label>
          <Input
            type="date"
            value={formData.expiresAt}
            onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
          />
        </div>
      </div>

      {/* IP Whitelist */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          IP Whitelist (optional)
        </label>
        <div className="space-y-2">
          {formData.ipWhitelist.map((ip, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={ip}
                onChange={(e) => handleIpWhitelistChange(index, e.target.value)}
                placeholder="192.168.1.0/24"
                className="flex-1"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeIpWhitelist(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addIpWhitelist}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add IP Range
          </Button>
        </div>
      </div>

      {/* User Agent */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          User Agent (optional)
        </label>
        <Input
          value={formData.userAgent}
          onChange={(e) => setFormData(prev => ({ ...prev, userAgent: e.target.value }))}
          placeholder="MyApp/1.0.0"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="submit" className="flex-1">
          {apiKey ? 'Update API Key' : 'Create API Key'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default APIKeysManagement;
